import React, { useState, useEffect } from 'react';
// import { useDispatch } from 'react-redux';
// import { setToast } from '../../../store/ToastManagement';
import { Container, Grid, Segment, Checkbox } from 'semantic-ui-react';
import { Button } from 'formik-semantic-ui';
import { useCurrentUser } from '../../../_services/hooks/user';
import { servicesAPI, useCustomerPreferences } from '../../../_services';
import {
  getLocalAuthorityInfo,
  isDateAfter,
  dbUTCDatetimeToLocalTime
} from '../../../utils';
import { CardDetailsModal } from '../../../_components/users/';
import '../customerDetail_styles.css';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';

const moment = extendMoment(Moment);

const formatCurrencyAmount = (currency, amount) => {
  if (isNaN(amount)) return '...';

  // Convert balance to pounds
  // e.g. account.account.balance_whpence 130750 = £1.31
  const abs_amount_pence = Math.abs(amount);
  const abs_amount_pounds = Math.ceil(abs_amount_pence / 100);

  const dec = String(abs_amount_pounds).split('.')[1];
  return (
    (amount < 0 ? '-' : '') +
    currency +
    abs_amount_pounds.toFixed(dec && dec.length > 2 ? dec.length : 2)
  );
};

const isBrent = contract => contract.localAuthority.name === 'Brent';

const isCamden = contract => contract.localAuthority.name === 'Camden';

const isBarnet = contract => contract.localAuthority.name === 'Barnet';

const isHarrowOrBexley = contract =>
  ['Harrow', 'Bexley'].includes(contract.localAuthority.name);

// NOTE: commercial (non-trial) CONTRACT went live 2023.06.05, but the pricing starts from 2023.06.13
const isNewBrentOrCamdenCustomer = (currentUser, contract) =>
  (isBrent(contract) || isCamden(contract)) &&
  isDateAfter(currentUser.created_datetime, '2023-06-05T00:00:00,Z');

const getPhone = customer =>
  customer.home_tel_no ? customer.home_tel_no : customer.mob_tel_no;

const getSignatureDate = timestamp => {
  if (!timestamp) return '';

  const bits = String(timestamp)
    .split('T')[0]
    .split('-');
  return bits[2] + '.' + bits[1] + '.' + bits[0];
};

const getSignatureTime = timestamp => {
  return timestamp ? dbUTCDatetimeToLocalTime(String(timestamp)) : '';
};

export const HUBOnboard = () => {
  const { currentUser, customerId } = useCurrentUser();
  const {
    customerPreferences,
    setCustomerPreferences
  } = useCustomerPreferences(customerId);

  const [customer, setCustomer] = useState(null);
  const [account, setAccount] = useState(null);
  const [paidInitialPayment, setPaidInitialPayment] = useState(false);
  const [stage, setStage] = useState('');
  const [contract, setContract] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    localAuthority: {
      name: '',
      description: ''
    },
    depositType: '',
    depositDescription: '',
    depositAmount: 0,
    installationSupplementAmount: 0,
    monthlyPaymentAmount: 0,
    durationMonths: 0,
    startDate: '',
    startTime: '',
    endDate: ''
  });

  // const dispatch = useDispatch();
  // const displayMessage = (title, message, success) =>
  //   dispatch(
  //     setToast({
  //       title: title,
  //       success: success,
  //       body: message
  //     })
  //   );

  useEffect(() => {
    if (!customerId) return;

    servicesAPI
      .CustomerServices('getById', customerId)
      .then(customer => setCustomer(customer));

    servicesAPI
      .BillingServices('getAccountByCustomerId', customerId)
      .then(account => {
        setAccount(account);
        setPaidInitialPayment(account.deposit_amount !== 0);
      });
  }, [customerId]);

  useEffect(() => {
    if (
      !currentUser ||
      !customer ||
      !customer.hasOwnProperty('agreed_to_terms_datetime')
    )
      return;

    servicesAPI.AdminServices('getContract', customer.id).then(response => {
      const newContract = {
        ...contract,
        customerName: customer.forename + ' ' + customer.surname,
        customerPhone: getPhone(customer),
        customerEmail: customer.email,
        localAuthority: getLocalAuthorityInfo(customer),
        depositType: '',
        depositDescription: '',
        depositAmount: response ? response.initial_payment_amount : 0,
        installationSupplementAmount: response
          ? response.installation_payment_amount
          : 0,
        monthlyPaymentAmount: response ? response.monthly_payment_amount : 0,
        durationMonths: response ? response.minimum_term_months : 0,
        startDate: getSignatureDate(customer.agreed_to_terms_datetime),
        startTime: getSignatureTime(customer.agreed_to_terms_datetime),
        endDate: getSignatureDate(response.end_datetime),
        paidInitialPayment: paidInitialPayment
      };

      if (isBarnet(newContract)) {
        newContract.depositType = 'ChargingCredit';
        newContract.depositDescription =
          'Trojan Energy HUB user charging credit';
      } else if (
        isNewBrentOrCamdenCustomer(currentUser, newContract) ||
        isHarrowOrBexley(newContract)
      ) {
        newContract.depositType = 'HubInitialPayment';
        newContract.depositDescription =
          'Trojan Energy HUB user initial payment';
      }

      setContract(newContract);
      setStage(!newContract.startDate ? '1_SUMMARY' : '4_ACCEPTED_T&CS');
    });
  }, [currentUser, customer, paidInitialPayment]);

  const signContract = registeredCardOrPaid => {
    // Audit
    const name =
      customer.forename && customer.surname
        ? customer.forename + ' ' + customer.surname
        : '';
    const localAuthority = contract.localAuthority.name;

    const emailSubject = `${localAuthority} customer ${name} accepted T&Cs`;
    const emailBody = `${localAuthority} customer ${name} has accepted the legal terms and conditions`;

    const values = {
      audit: {
        user_id: currentUser.id,
        activity: 'CustomerContractSign',
        entity_type: 'CUSTOMER',
        entity_id: customer.id,
        notes: `Accept legal terms`,
        status: 'Completed',
        email_subject: emailSubject,
        email_body: emailBody
      }
    };

    const documentId = customer.terms_document_id;

    servicesAPI
      .CustomerServices('acceptTerms', values, customerId, documentId)
      .then(response => {
        const datetime = moment().format('YYYY-MM-DDTHH:mm:00') + ',Z';
        const customer = {
          ...values,
          agreed_to_terms_datetime: datetime,
          customerId
        };

        setContract({
          ...contract,
          startDate: getSignatureDate(customer.agreed_to_terms_datetime),
          startTime: getSignatureTime(customer.agreed_to_terms_datetime)
        });

        servicesAPI.AdminServices('startContract', customerId);
      });
  };

  // const cardInitialPayment = (type, description, amount, supplementAmount) => {
  //   // Audit
  //   const name =
  //     customer.forename && customer.surname
  //       ? customer.forename + ' ' + customer.surname
  //       : '';
  //   const localAuthority = contract.localAuthority.name;
  //   const total = amount + supplementAmount;
  //
  //   const emailSubject = `${localAuthority} HUB customer ${name} initial payment`;
  //   const emailBody = `New ${localAuthority} HUB customer ${name} has made their initial ${formatCurrencyAmount(
  //     '£',
  //     total
  //   )} payment.`;
  //
  //   const notes = `${description} ${formatCurrencyAmount('£', total)}`;
  //
  //   const values = {
  //     transaction_type: type,
  //     customer_id: customerId,
  //     lance_customer_id: null,
  //     account_number: currentUser.user_name,
  //     description,
  //     amount,
  //     supplement_amount: supplementAmount,
  //     audit: {
  //       user_id: currentUser.id,
  //       activity: 'BillingPayment',
  //       entity_type: 'CUSTOMER',
  //       entity_id: customerId,
  //       notes: notes,
  //       email_subject: emailSubject,
  //       email_body: emailBody
  //     }
  //   };
  //
  //   servicesAPI
  //     .BillingServices('oneOffPayment', values, customerId)
  //     .then(response => {
  //       displayMessage('Success', 'Payment successful!', true);
  //       setPaidInitialPayment(true);
  //       signContract(true);
  //       setStage('3_AGREED_TO_TERMS');
  //     })
  //     .catch(response =>
  //       displayMessage(
  //         'Error',
  //         'Payment failed. If you are unaware why, please contact us',
  //         false
  //       )
  //     );
  // };

  const cardRegisteredCallback = () => {
    servicesAPI
      .BillingServices('getAccountByCustomerId', customerId)
      .then(account => {
        setAccount(account);
        setPaidInitialPayment(account.deposit_amount !== 0);

        if (contract.depositAmount) {
          // If there is an initial payment, take it first before signing contract
          // cardInitialPayment(
          //   contract.depositType,
          //   contract.depositDescription,
          //   contract.depositAmount,
          //   contract.installationSupplementAmount
          // );
        } else {
          // Otherwise, sign contract immediately
          signContract(true);
          setStage('3_AGREED_TO_TERMS');
        }
      });
  };

  if (!customer) return <div>Loading...</div>;

  return (
    <div>
      <Container>
        <Grid centered padded>
          <Grid.Column style={{ maxWidth: 500 }}>
            {stage === '1_SUMMARY' && (
              <Segment>
                Please review and accept the Trojan Energy Terms of Service
                <br />
                <br />
                <div style={{ textAlign: 'center' }}>
                  <Button
                    style={{ backgroundColor: '#03b5ae', color: '#ffffff' }}
                    onClick={(values, formikApi) =>
                      window.open(
                        'https://trojan.energy/consumerterms',
                        '_blank'
                      )
                    }
                  >
                    View Trojan HUB Terms of Service
                  </Button>
                </div>
                <br />
                By continuing, you confirm that you have read and agree to the
                Trojan Energy Privacy Policy.
                <br />
                <br />
                <div style={{ textAlign: 'center' }}>
                  <Button
                    style={{ backgroundColor: '#03b5ae', color: '#ffffff' }}
                    onClick={(values, formikApi) => {
                      window.open('https://trojan.energy/privacy', '_blank');
                    }}
                  >
                    View Privacy Policy
                  </Button>
                  <br />
                  <br />
                  <Button
                    color="olive"
                    onClick={(values, formikApi) => {
                      if (account && account.card_last4_digits === '')
                        setStage('2_MAKE_PAYMENT');
                      else {
                        signContract(false);
                        setStage('3_AGREED_TO_TERMS');
                      }
                    }}
                  >
                    Accept and sign up
                  </Button>
                </div>
              </Segment>
            )}
            {stage === '2_MAKE_PAYMENT' && (
              <Segment>
                Thank you for accepting the Trojan Energy Terms of Service.
                <br />
                <br />
                To pay for your charging sessions, you will need to register a
                payment card in the Trojan Energy Portal. As a Trojan HUB
                customer, to order your Trojan Lance you will make an upfront
                payment of {formatCurrencyAmount('£', contract.depositAmount)} -
                this will be added to your Trojan Energy Account as credit for
                use against future charging sessions.
                <br />
                <br />
                <div style={{ textAlign: 'center' }}>
                  <CardDetailsModal
                    buttonText={`Register payment card and pay ${formatCurrencyAmount(
                      '£',
                      contract.depositAmount
                    )} for my Lance`}
                    callback={cardRegisteredCallback}
                    reload={false}
                    edit={true}
                  />
                </div>
              </Segment>
            )}
            {stage === '3_AGREED_TO_TERMS' && (
              <Segment>
                <div>
                  Thank you and welcome to Trojan Energy!
                  <br />
                  <br />
                  Your Lance has been ordered and should be delivered to you
                  within ten working days.
                  <br />
                  <br />
                  While you wait, please review the Trojan HUB user manual and
                  training videos available.
                  <br />
                  <br />
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      color="olive"
                      onClick={(values, formikApi) => {
                        window.open(
                          'https://trojan.energy/charging-manual',
                          '_blank'
                        );
                      }}
                    >
                      User manual
                    </Button>
                    <br />
                    <br />
                    <Button
                      color="olive"
                      onClick={(values, formikApi) => {
                        window.open(
                          'https://trojan.energy/resources/how-to-charge',
                          '_blank'
                        );
                      }}
                    >
                      Training videos
                    </Button>
                  </div>
                </div>
              </Segment>
            )}
            {stage === '4_ACCEPTED_T&CS' && (
              <Segment>
                <br />
                <div style={{ textAlign: 'center' }}>
                  <Button
                    style={{ backgroundColor: '#03b5ae', color: '#ffffff' }}
                    onClick={(values, formikApi) =>
                      window.open(
                        'https://trojan.energy/consumerterms',
                        '_blank'
                      )
                    }
                  >
                    View Trojan HUB Terms of Service
                  </Button>
                </div>
                <br />
                <div style={{ textAlign: 'center' }}>
                  <Button
                    style={{ backgroundColor: '#03b5ae', color: '#ffffff' }}
                    onClick={(values, formikApi) => {
                      window.open('https://trojan.energy/privacy', '_blank');
                    }}
                  >
                    View Privacy Policy
                  </Button>
                  <br />
                  <br />
                  <Checkbox
                    label="Opt out of marketing"
                    checked={customerPreferences?.pref_marketing_opt_in}
                    onChange={e => {
                      const values = {
                        ...customerPreferences,
                        pref_marketing_opt_in: !customerPreferences.pref_marketing_opt_in,
                        id: customerId
                      };

                      servicesAPI
                        .CustomerServices(
                          'updatePreferences',
                          values,
                          customerId
                        )
                        .then(response => setCustomerPreferences(values));
                    }}
                  />
                </div>
                <br />
                You agreed to the Trojan Energy Terms of Service on{' '}
                <b>{contract.startDate}</b> at <b>{contract.startTime}</b>.
                {contract.endDate ? (
                  <>
                    <br />
                    This Contract was cancelled on <b>{contract.endDate}</b>
                  </>
                ) : (
                  ''
                )}
              </Segment>
            )}
          </Grid.Column>
        </Grid>
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
      </Container>
    </div>
  );
};
